import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { FormItem, FormLabel, FormControl, FormMessage } from "../ui/form";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  useGetComputeCapacityQuery,
  useCreateComputeCapacityMutation,
  useUpdateComputeCapacityMutation,
} from "../../store/api/computeCapacityApi";

const formSchema = z.object({
  computeId: z.string().min(1, "Compute ID is required"),
  // label: z.string().optional().nullable(),
  totalUnit: z.number().min(0, "Total units must be greater than 0").optional(),
  availableUnit: z.any().readonly(),
  allocatedUnit: z
    .number()
    .min(0, "Allocated units cannot be negative")
    .optional(),
  deployedUnit: z
    .number()
    .min(0, "Allocated units cannot be negative")
    .optional(),
});

const ComputeCapacityFormModal = ({
  isOpen,
  onClose,
  id = null,
  mode = "create",
}) => {
  const isEditMode = mode === "edit";

  const {
    data: computeData,
    isLoading: isLoadingCompute,
    error: fetchError,
  } = useGetComputeCapacityQuery(id, {
    skip: !isOpen || !isEditMode,
  });

  const [createCapacity, { isLoading: isCreating }] =
    useCreateComputeCapacityMutation();
  const [updateCapacity, { isLoading: isUpdating }] =
    useUpdateComputeCapacityMutation();

  const isSubmitting = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      computeId: "",
      label: "",
      totalUnit: 0,
      availableUnit: 0,
      allocatedUnit: 0,
      deployedUnit: 0,
    },
  });

  useEffect(() => {
    if (isEditMode && computeData) {
      reset({
        computeId: computeData.computeId,
        // label: computeData.label,
        totalUnit: computeData.totalUnit,
        availableUnit: computeData.availableUnit,
        allocatedUnit: computeData.allocatedUnit,
        deployedUnit: computeData.deployedUnit,
      });
    } else if (!isEditMode) {
      reset({
        computeId: "",
        // label: "",
        totalUnit: 0,
        availableUnit: 0,
        allocatedUnit: 0,
        deployedUnit: 0,
      });
    }
  }, [computeData, reset, isEditMode]);

  const onSubmit = async (values) => {
    try {
      if (isEditMode) {
        await updateCapacity({
          id,
          ...values,
        }).unwrap();
      } else {
        await createCapacity(values).unwrap();
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} compute capacity:`,
        error
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto pt-4">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit" : "Create"} Compute Capacity
          </DialogTitle>
        </DialogHeader>

        {isEditMode && isLoadingCompute ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">Loading data...</span>
          </div>
        ) : isEditMode && fetchError ? (
          <div className="text-center py-8 text-destructive">
            Error loading compute capacity data. Please try again.
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormItem>
              <FormLabel>Compute ID</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("computeId")}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.computeId && (
                <FormMessage>{errors.computeId.message}</FormMessage>
              )}
            </FormItem>
            {/* <FormItem>
              <FormLabel>Compute Label</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("label")}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.label && (
                <FormMessage>{errors.label.message}</FormMessage>
              )}
            </FormItem> */}
            <FormItem>
              <FormLabel>Total Units</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("totalUnit", { valueAsNumber: true })}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.totalUnit && (
                <FormMessage>{errors.totalUnit.message}</FormMessage>
              )}
            </FormItem>
            <FormItem>
              <FormLabel>Available Units</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("availableUnit", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  readOnly={true}
                />
              </FormControl>
              {errors.availableUnit && (
                <FormMessage>{errors.availableUnit.message}</FormMessage>
              )}
            </FormItem>
            <FormItem>
              <FormLabel>Allocated Units</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("allocatedUnit", { valueAsNumber: true })}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.allocatedUnit && (
                <FormMessage>{errors.allocatedUnit.message}</FormMessage>
              )}
            </FormItem>
            <FormItem>
              <FormLabel>Deployed Units</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("deployedUnit", { valueAsNumber: true })}
                  disabled={isSubmitting}
                />
              </FormControl>
              {errors.deployedUnit && (
                <FormMessage>{errors.deployedUnit.message}</FormMessage>
              )}
            </FormItem>
            <DialogFooter>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEditMode ? "Saving..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Save Changes"
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComputeCapacityFormModal;
